import { ApolloError } from '@apollo/client'
import { assign, AssignAction, createMachine } from 'xstate'
import { CartCustomerInput, CartFieldsFragment, ShippingMethodsQuery, UpdateCartMutationVariables } from 'types/graphql'
import { ShippingMethodActor } from 'modules/checkout/context/shippingMethodMachine'

interface CartMachineContext {
  cart: CartFieldsFragment | null
  shippingMethodActors: ShippingMethodActor[]
  error?: string | null
  mutationError?: ApolloError | null
  // A map of bag id to shipping method id
  shippingMethods: Record<number, string | null>
  checkoutFormStep: CheckoutSteps
}

type CartEvents =
  | { type: 'UPDATE_CART'; cartUpdateInput: Omit<UpdateCartMutationVariables, 'id'> }
  | { type: 'CHECKOUT' }
  | { type: 'SAVE_CUSTOMER'; saveCustomerInput?: CartCustomerInput }
  | { type: 'EDIT_CUSTOMER' }
  | { type: 'SELECT_SHIPPING_METHOD'; bagId: string; shippingMethodId: string }
  | { type: 'SHIPPING_METHOD_UPDATED_CART'; data: CartFieldsFragment }
  | { type: 'RESET_CART' }
  | { type: 'RETURN_TO_IDLE' }

type CartServices = {
  updateCart: { data: CartFieldsFragment | null }
  saveCustomer: { data: CartFieldsFragment }
  getShippingMethods: { data: ShippingMethodsQuery }
  selectShippingMethods: { data: CartFieldsFragment }
  createCart: { data: CartFieldsFragment }
  initializeCart: { data: CartFieldsFragment | null }
  getPaymentIntentClientSecret: { data: CartFieldsFragment }
}

type CartActions = {
  handleError: (ctx: CartMachineContext) => void
  trackCheckoutStarted: (ctx: CartMachineContext) => void
  trackCustomerStepViewed: (ctx: CartMachineContext) => void
  trackCustomerStepCompleted: (ctx: CartMachineContext) => void
  trackPaymentStepViewed: (ctx: CartMachineContext) => void
  trackPaymentStepCompleted: (ctx: CartMachineContext) => void
  trackPaymentInfoEntered: (ctx: CartMachineContext) => void
  trackOrderCompleted: (ctx: CartMachineContext) => void
  registerShippingMethodMachines: AssignAction<CartMachineContext, { type: 'done.invoke.gettingShippingMethods'; data: ShippingMethodsQuery }>
  type: 'trigger'
}

export enum CheckoutSteps {
  customer = 'customer',
  shippingAndPayment = 'shippingAndPayment',
  complete = 'complete'
}

export enum FormStateTags {
  blocked = 'blocked'
}

export const cartMachine = createMachine(
  {
    id: 'cartMachine',
    tsTypes: {} as import('./cartMachine.typegen').Typegen0,
    schema: {
      context: {} as CartMachineContext,
      events: {} as CartEvents,
      services: {} as CartServices,
      actions: {} as CartActions
    },
    context: {
      cart: null,
      shippingMethodActors: [],
      shippingMethods: {},
      checkoutFormStep: CheckoutSteps.customer
    },
    predictableActionArguments: true,
    initial: 'initializing',
    on: {
      RETURN_TO_IDLE: 'idle'
    },
    states: {
      initializing: {
        invoke: {
          id: 'initializeCart',
          src: 'initializeCart',
          onDone: [
            {
              target: 'checkout',
              actions: ['setCart'],
              cond: 'onCheckoutPage'
            },
            {
              target: 'idle',
              actions: ['setCart']
            }
          ]
        }
      },
      idle: {
        id: 'idle',
        on: {
          UPDATE_CART: [
            { target: 'updatingCart', cond: 'cartInContext' },
            { target: 'creatingCart', cond: 'cartNotInContext' }
          ],
          CHECKOUT: {
            target: 'checkout'
          }
        }
      },
      creatingCart: {
        tags: [FormStateTags.blocked],
        invoke: {
          id: 'createCart',
          src: 'createCart',
          onDone: { actions: ['setCart'], target: 'idle' },
          onError: { target: 'idle', actions: ['handleError'] }
        }
      },
      updatingCart: {
        tags: [FormStateTags.blocked],
        id: 'updatingCart',
        invoke: {
          id: 'updateCart',
          src: 'updateCart',
          onDone: [
            { actions: ['setCart'], target: 'idle', cond: 'notOnCheckoutPage' },
            { actions: ['setCart'], target: 'checkout', cond: 'onCheckoutPage' }
          ],
          onError: { target: 'idle', actions: ['handleError'] }
        }
      },
      checkout: {
        initial: 'transitionToCorrectStep',
        states: {
          transitionToCorrectStep: {
            always: [
              // If the customer has completed some form of the checkout procedure we want to drop them at the appropriate location
              {
                target: 'customerForm',
                cond: 'cartIsNotCompleteAndCustomerIsNull',
                actions: ['trackCheckoutStarted']
              },
              { target: 'gettingPaymentIntentClientSecret', cond: 'cartIsNotCompleteWithCustomerAndNoPhysicalProduct' },
              { target: 'gettingShippingMethods', cond: 'cartIsNotCompleteAndCustomerIsFilled' },
              { target: 'complete', cond: 'cartIsComplete', actions: ['trackOrderCompleted'] }
            ]
          },
          customerForm: {
            entry: ['setFormToCustomer', 'setRouterToCustomerForm', 'trackCustomerStepViewed'],
            on: {
              SAVE_CUSTOMER: {
                target: 'savingCustomer'
              },
              UPDATE_CART: {
                target: '#updatingCart'
              }
            }
          },
          savingCustomer: {
            tags: [FormStateTags.blocked],
            entry: ['setFormToCustomer', 'clearErrors'],
            invoke: {
              id: 'saveCustomer',
              src: 'saveCustomer',
              onDone: [
                {
                  target: 'gettingPaymentIntentClientSecret',
                  actions: ['setCart', 'trackCustomerStepCompleted'],
                  cond: 'cartIsNotCompleteWithCustomerAndNoPhysicalProduct'
                },
                {
                  target: 'gettingShippingMethods',
                  actions: ['setCart', 'trackCustomerStepCompleted'],
                  cond: 'cartIsValid'
                },
                {
                  target: 'customerForm',
                  actions: ['setCart'],
                  cond: 'cartIsInvalid'
                }
              ],
              onError: {
                target: 'customerForm',
                actions: ['setMutationErrors', 'handleError']
              }
            }
          },
          gettingShippingMethods: {
            tags: [FormStateTags.blocked],
            onEntry: ['setFormToShippingAndPayment', 'clearErrors'],
            // Invoke promise that gets shipping methods and moves to payments form
            invoke: {
              id: 'getShippingMethods',
              src: 'getShippingMethods',
              onDone: {
                target: 'shippingAndPaymentForm',
                actions: ['registerShippingMethodMachines']
              },
              onError: {
                target: 'customerForm',
                actions: 'handleError'
              }
            }
          },
          gettingPaymentIntentClientSecret: {
            tags: [FormStateTags.blocked],
            onEntry: ['setFormToShippingAndPayment', 'clearErrors'],
            invoke: {
              id: 'getPaymentIntentClientSecret',
              src: 'getPaymentIntentClientSecret',
              onDone: {
                target: 'shippingAndPaymentForm',
                actions: ['setCart']
              },
              onError: {
                target: 'customerForm',
                actions: 'handleError'
              }
            }
          },
          shippingAndPaymentForm: {
            entry: ['setFormToShippingAndPayment', 'setRouterToShippingAndPaymentForm', 'trackPaymentStepViewed'],
            on: {
              EDIT_CUSTOMER: {
                target: 'customerForm'
              },
              UPDATE_CART: {
                target: '#updatingCart'
              },
              SELECT_SHIPPING_METHOD: [
                {
                  target: 'selectingShippingMethods',
                  actions: ['setShippingMethod'],
                  cond: 'hasSelectedShippingMethodForEachBag'
                },
                { actions: 'setShippingMethod' }
              ],
              SHIPPING_METHOD_UPDATED_CART: {
                actions: ['setCart']
              }
            }
          },
          selectingShippingMethods: {
            tags: [FormStateTags.blocked],
            entry: ['setFormToShippingAndPayment', 'clearErrors'],
            invoke: {
              id: 'selectShippingMethods',
              src: 'selectShippingMethods',
              onDone: { target: 'shippingAndPaymentForm', actions: ['setCart'] },
              onError: { target: 'shippingAndPaymentForm', actions: ['handleError'] }
            }
          },
          complete: {
            entry: ['setFormToComplete', 'setRouterToComplete', 'clearCartIdFromStorage', 'stopShippingMethodActors'],
            on: {
              RESET_CART: {
                target: '#idle',
                actions: ['stopShippingMethodActors', 'resetContext']
              }
            }
          }
        }
      }
    }
  },
  {
    actions: {
      setCart: assign((_, event) => ({
        cart: event.data
      })),
      setMutationErrors: assign((_, event) => ({
        mutationError: event.data as ApolloError
      })),
      clearErrors: assign((_) => ({
        error: null,
        mutationError: null
      })),
      stopShippingMethodActors: (context) => {
        context.shippingMethodActors.map((actor) => actor.stop?.())
      },
      resetContext: assign((_) => ({
        cart: null,
        error: null,
        mutationError: null,
        shippingMethodActors: [],
        shippingMethods: {}
      })),

      setShippingMethod: assign((context, event) => ({
        shippingMethods: {
          ...context.shippingMethods,
          [event.bagId]: event.shippingMethodId
        }
      })),

      setFormToCustomer: assign((_) => ({
        checkoutFormStep: CheckoutSteps.customer
      })),

      setFormToShippingAndPayment: assign((_) => ({
        checkoutFormStep: CheckoutSteps.shippingAndPayment
      })),

      setFormToComplete: assign((_) => ({
        checkoutFormStep: CheckoutSteps.complete
      }))
    },
    guards: {
      cartIsNotCompleteAndCustomerIsNull: (context) => {
        const { cart } = context
        return !cart || (cart.status !== 'COMPLETED' && cart.customer === null)
      },

      cartIsNotCompleteWithCustomerAndNoPhysicalProduct: (context) => {
        const { cart } = context

        return !!cart && cart.status !== 'COMPLETED' && !cart.hasPhysicalVariants
      },

      cartIsNotCompleteAndCustomerIsFilled: (context) => {
        const { cart } = context
        return !!cart && cart.status !== 'COMPLETED' && cart.customer !== null
      },

      cartIsComplete: (context) => {
        return context.cart?.status === 'COMPLETED'
      },

      cartInContext: (context) => {
        return !!context.cart
      },

      cartNotInContext: (context) => {
        return !context.cart
      },

      cartIsValid: (_, event) => event.data.errors.length === 0,
      cartIsInvalid: (_, event) => event.data.errors.length !== 0,

      hasSelectedShippingMethodForEachBag: (context, event) => {
        const bagIds = context.cart?.bags.map((bag) => bag.id) ?? []
        const bagIdsWithSelectedShippingMethods = Object.entries(context.shippingMethods)
          .concat([[event.bagId, event.shippingMethodId]])
          // Object.entries converts the key of the object to a string so we have to convert it bag again
          .map(([bagId]) => Number(bagId))

        return bagIds.every((bagId) => bagIdsWithSelectedShippingMethods.includes(bagId))
      }
    }
  }
)
